import { Button } from "@/components/ui/button";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton.tsx";
import type { IconButtonProps } from "@/components/ui/icon-button.tsx";
import { FilterHeader } from "@/components/ui/molecules/FilterHeader.tsx";
import SegmentListItem from "@/components/ui/molecules/SegmentListItem.tsx";
import { ResizablePanel } from "@/components/ui/resizable.tsx";
import { cn } from "@/lib/utils";
import { updateSegment } from "@/store/share-of-voice/segment/segment-async.actions";
import { useSortedSegments } from "@/store/share-of-voice/segment/segment.selectors";
import useSegmentStore from "@/store/share-of-voice/segment/segment.slice";
import {
  BookmarksSimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import SegmentMenu from "./SegmentMenu";

/**
 * Hold Segment List Items
 */
const SegmentContainer = () => {
  const setIsCreateOrEditSegmentModalOpen = useSegmentStore(
    (state) => state.setIsCreateOrEditSegmentModalOpen,
  );
  const segments = useSortedSegments();
  const isFetchingSegments = useSegmentStore(
    (state) => state.isFetchingSegments,
  );

  const segmentActions: IconButtonProps[] = [
    {
      icon: <PlusIcon />,
      "aria-label": "Add a new segment",
      tooltipLabel: "Create a new segment",
      size: "sm",
      variant: "ghost",
      onPress: () => setIsCreateOrEditSegmentModalOpen(true),
    },
  ];

  const renderSegmentContent = () => {
    if (isFetchingSegments) {
      return (
        <ItemSkeleton
          numberOfItems={3}
          showNestedItems={false}
          containerClassName="space-y-1 p-2"
          groupItemClassName="h-6"
        />
      );
    }

    if (segments.length === 0) {
      return (
        <Button
          variant="none"
          className="gap-2 flex text-xxs! text-gray-600 font-normal px-6 py-1"
          size={"none"}
          onPress={() => setIsCreateOrEditSegmentModalOpen(true)}
        >
          <PlusIcon className="h-3 w-3 text-gray-600" />
          Add a segment
        </Button>
      );
    }

    return (
      <div className="flex flex-col pl-3.5">
        {segments.map((segment) => (
          <SegmentListItem
            key={segment.id}
            color={segment.hexColor}
            label={segment.name}
            isHidden={!segment.isVisible}
            size="sm"
            className="group hover:-ml-3.5 hover:pl-3.5"
            secondaryAction={{
              icon: segment.isVisible ? (
                <EyeIcon size={14} />
              ) : (
                <EyeSlashIcon size={14} />
              ),
              "aria-label": "View/ Hide segment",
              size: "md",
              className: cn(
                "text-gray-600",
                segment.isVisible &&
                  "opacity-0 group-hover:opacity-100 transition-opacity",
              ),
              variant: "ghost",
              onClick: () => {
                void updateSegment(
                  {
                    id: segment.id,
                    isVisible: !segment.isVisible,
                  },
                  {
                    showSuccessToast: false,
                  },
                );
              },
              tooltipLabel: "Toggle visibility",
            }}
          >
            <SegmentMenu segment={segment} />
          </SegmentListItem>
        ))}
      </div>
    );
  };

  return (
    <ResizablePanel minSize={5} defaultSize={15}>
      <FilterHeader
        icon={<BookmarksSimpleIcon />}
        label={"Segments"}
        actions={segmentActions}
      />
      <div className="overflow-y-scroll h-full scrollbar-thin scrollbar-thumb-gray-150">
        {renderSegmentContent()}
      </div>
    </ResizablePanel>
  );
};

export default SegmentContainer;
